import React, { useState, useEffect } from "react";
import {
  Grid,
  Card,
  CardContent,
  Typography,
  Box,
  Button,
  List,
  ListItem,
  IconButton,
  ListItemText,
  Chip,
  Alert,
  CircularProgress,
  Switch,
  FormControlLabel,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Paper,
} from "@mui/material";
import {
  Add as AddIcon,
  Delete as DeleteIcon,
  Refresh as RefreshIcon,
  CheckCircle as CheckCircleIcon,
  Security as SecurityIcon,
  Upload as UploadIcon,
} from "@mui/icons-material";
import { googleApi } from "../../../../services/api/prepbusiness/google_credentials";

const GoogleCredentials = () => {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(null);
  const [autoRefreshInfo, setAutoRefreshInfo] = useState(null);

  // Google
  const [googleCredentials, setGoogleCredentials] = useState([]);
  const [credentialDialog, setCredentialDialog] = useState(false);
  const [editingCredential, setEditingCredential] = useState(null);
  const [authUrlDialog, setAuthUrlDialog] = useState(false);
  const [authUrl, setAuthUrl] = useState("");
  const [credentialForm, setCredentialForm] = useState({
    scopes: [],
  });
  const [uploadedFile, setUploadedFile] = useState(null);

  const getErrorMessage = (error) => {
    if (typeof error === "string") return error;

    // Handle axios error structure
    if (error.response?.data?.detail) {
      const detail = error.response.data.detail;
      if (typeof detail === "string") return detail;
      if (Array.isArray(detail)) {
        // Handle validation errors array
        return detail
          .map((err) =>
            typeof err === "string"
              ? err
              : err.msg
                ? `${err.loc?.join(".")} ${err.msg}`
                : JSON.stringify(err),
          )
          .join("; ");
      }
      return JSON.stringify(detail);
    }

    if (error.response?.data?.message) {
      return typeof error.response.data.message === "string"
        ? error.response.data.message
        : JSON.stringify(error.response.data.message);
    }

    if (error.message) {
      return typeof error.message === "string"
        ? error.message
        : JSON.stringify(error.message);
    }

    return JSON.stringify(error);
  };

  const availableScopes = [
    {
      value: "https://www.googleapis.com/auth/gmail.readonly",
      label: "Gmail Read-Only",
      description: "Read all resources and their metadata—no write operations.",
      usage: "Restricted",
      defaultEnabled: true,
    },
    // Google Sheets API scopes
    {
      value: "https://www.googleapis.com/auth/spreadsheets",
      label: "Google Sheets Full Access",
      description:
        "Alle Ihre Google Tabellen-Tabellen aufrufen, bearbeiten, erstellen oder löschen",
      usage: "Sheets",
      defaultEnabled: true,
    },
    {
      value: "https://www.googleapis.com/auth/spreadsheets.readonly",
      label: "Google Sheets Read-Only",
      description: "Alle Ihre Google-Tabellen aufrufen",
      usage: "Sheets",
      defaultEnabled: false,
    },
  ];

  const handleFileUpload = (event) => {
    const file = event.target.files[0];
    if (file && file.type === "application/json") {
      setUploadedFile(file);
    } else {
      setError("Please select a valid JSON file");
    }
  };

  useEffect(() => {
    // Load initial data with error handling
    const initializeData = async () => {
      try {
        // Load all data in parallel but handle each error separately
        const loadOperations = [
          loadGoogleCredentials().catch((error) => {
            console.error("Failed to load Google credentials on init:", error);
            return null; // Continue with other operations
          }),
        ];

        await Promise.allSettled(loadOperations);
      } catch (error) {
        console.error("Unexpected error during initialization:", error);
        setError(getErrorMessage(error));
      }
    };

    initializeData();

    // Initialize form states with default scopes only once on mount
    const defaultScopes = availableScopes
      .filter((scope) => scope.defaultEnabled)
      .map((scope) => scope.value);
    setCredentialForm((prev) => ({
      ...prev,
      scopes: defaultScopes,
    }));
  }, []);

  //Google
  const loadGoogleCredentials = async (forceRefresh = false) => {
    try {
      setLoading(true);
      setError(null);

      const CACHE_KEY = "google_credentials_cache";
      const CACHE_DURATION = 300 * 1000; // 300 seconds

      if (!forceRefresh) {
        const cached = localStorage.getItem(CACHE_KEY);
        if (cached) {
          try {
            const { data, timestamp } = JSON.parse(cached);
            if (Date.now() - timestamp < CACHE_DURATION) {
              setGoogleCredentials(data);
              await autoRefreshExpiredTokens(data);
              setLoading(false);
              return;
            }
          } catch (e) {
            console.error("Error parsing cache", e);
            localStorage.removeItem(CACHE_KEY);
          }
        }
      }

      const response = await googleApi.getCredentials();
      const credentials = response.data || [];
      setGoogleCredentials(credentials);

      localStorage.setItem(
        CACHE_KEY,
        JSON.stringify({
          data: credentials,
          timestamp: Date.now(),
        }),
      );

      // Auto-refresh expired tokens that have refresh_token available
      await autoRefreshExpiredTokens(credentials);
    } catch (error) {
      console.error("Failed to load Google credentials:", error);
      setError(getErrorMessage(error));
      // Set empty array on error to show "no credentials" message
      setGoogleCredentials([]);
    } finally {
      setLoading(false);
    }
  };

  const autoRefreshExpiredTokens = async (credentials) => {
    const expiredCredentials = credentials.filter((credential) => {
      if (!credential.expires_at || !credential.refresh_token) return false;
      const expiresAt = new Date(credential.expires_at);
      const now = new Date();
      return expiresAt.getTime() <= now.getTime(); // Token is expired
    });

    if (expiredCredentials.length === 0) return;

    setAutoRefreshInfo(
      `Auto-refreshing ${expiredCredentials.length} expired token(s)...`,
    );

    // Refresh expired tokens in parallel
    const refreshPromises = expiredCredentials.map(async (credential) => {
      try {
        console.log(
          `Refreshing token for: ${
            credential.authenticated_email || credential.client_id
          }`,
        );
        const response = await googleApi.refreshCredential(credential.id);

        if (response.data?.auth_url) {
          // If auth_url is returned, token refresh requires re-authorization
          console.log(
            `Token for ${
              credential.authenticated_email || credential.client_id
            } requires re-authorization`,
          );
          return { credential, needsReauth: true };
        } else {
          console.log(
            `Token for ${
              credential.authenticated_email || credential.client_id
            } refreshed successfully`,
          );
          return { credential, refreshed: true };
        }
      } catch (error) {
        console.error(
          `Failed to refresh token for ${
            credential.authenticated_email || credential.client_id
          }:`,
          error,
        );
        return { credential, error: true };
      }
    });

    const results = await Promise.allSettled(refreshPromises);
    const refreshedCount = results.filter(
      (r) => r.status === "fulfilled" && r.value?.refreshed,
    ).length;
    const reauthCount = results.filter(
      (r) => r.status === "fulfilled" && r.value?.needsReauth,
    ).length;

    if (refreshedCount > 0) {
      setSuccess(`${refreshedCount} token(s) refreshed automatically`);
    }
    if (reauthCount > 0) {
      setAutoRefreshInfo(
        `${reauthCount} token(s) need manual re-authorization`,
      );
    }

    // Reload credentials after refresh attempts
    try {
      const updatedResponse = await googleApi.getCredentials();
      setGoogleCredentials(updatedResponse.data || []);
    } catch (error) {
      console.error("Failed to reload credentials after auto-refresh:", error);
    }

    // Clear the auto-refresh info after 5 seconds
    setTimeout(() => setAutoRefreshInfo(null), 5000);
  };

  const handleCredentialSubmit = async () => {
    if (!uploadedFile) {
      setError("Please upload a credentials file");
      return;
    }

    try {
      setLoading(true);
      setError(null);

      const response = await googleApi.uploadCredentialsAndAuth(
        uploadedFile,
        credentialForm.scopes,
      );

      if (response.data?.auth_url) {
        // Show the auth URL dialog instead of just a success message
        setAuthUrl(response.data.auth_url);
        setAuthUrlDialog(true);
        setSuccess("Google OAuth flow initiated successfully!");
      } else {
        setSuccess(
          "Google OAuth flow initiated! Check the response for next steps.",
        );
      }

      setCredentialForm({
        scopes: ["https://www.googleapis.com/auth/gmail.readonly"],
      });
      setUploadedFile(null);
      setCredentialDialog(false);

      // Reload credentials after successful submission
      await loadGoogleCredentials();
    } catch (error) {
      console.error("Failed to start OAuth flow:", error);
      setError(getErrorMessage(error));
    } finally {
      setLoading(false);
    }
  };

  const deleteCredential = async (credentialId) => {
    try {
      setLoading(true);
      setError(null);

      await googleApi.deleteCredential(credentialId);

      await loadGoogleCredentials();
      setSuccess("Credential deleted successfully!");
    } catch (error) {
      console.error("Failed to delete credential:", error);
      setError(getErrorMessage(error));
    } finally {
      setLoading(false);
    }
  };

  const reauthorizeCredential = async (credentialId) => {
    try {
      setLoading(true);
      setError(null);

      const response = await googleApi.refreshCredential(credentialId);

      if (response.data?.auth_url) {
        setAuthUrl(response.data.auth_url);
        setAuthUrlDialog(true);
        setSuccess("Re-authorization initiated! Please authenticate again.");
      } else {
        setSuccess("Credential refreshed successfully!");
        await loadGoogleCredentials();
      }
    } catch (error) {
      console.error("Failed to reauthorize credential:", error);
      setError(getErrorMessage(error));
    } finally {
      setLoading(false);
    }
  };

  const handleReauthorize = async (credentialId) => {
    try {
      setLoading(true);
      setError(null);

      const response = await googleApi.reauthorizeCredential(credentialId);

      if (response.data?.auth_url) {
        setAuthUrl(response.data.auth_url);
        setAuthUrlDialog(true);
        setSuccess("Re-authorization initiated successfully!");
      } else {
        setError("Failed to get authorization URL");
      }
    } catch (error) {
      console.error("Failed to initiate re-authorization:", error);
      setError(getErrorMessage(error));
    } finally {
      setLoading(false);
    }
  };

  const toggleActiveCredential = async (credentialId) => {
    try {
      setLoading(true);
      setError(null);

      const response = await googleApi.toggleActiveCredential(credentialId);

      // Update the credential immediately in the state for instant feedback
      setGoogleCredentials((prev) =>
        prev.map(
          (cred) =>
            cred.id === credentialId
              ? { ...cred, active_omni: response.data.active_omni }
              : { ...cred, active_omni: false }, // Only one can be active at a time
        ),
      );

      setSuccess(
        `Credential ${
          response.data.active_omni ? "activated" : "deactivated"
        } successfully!`,
      );

      // Also reload to ensure consistency
      await loadGoogleCredentials();
    } catch (error) {
      console.error("Failed to toggle credential status:", error);
      setError(getErrorMessage(error));
      // Reload on error to ensure consistency
      await loadGoogleCredentials();
    } finally {
      setLoading(false);
    }
  };

  const handleAuthDialogClose = async () => {
    setAuthUrlDialog(false);

    // Wait 2 seconds to allow for any completed authentication
    setTimeout(async () => {
      await loadGoogleCredentials();
    }, 2000);
  };

  const getTokenStatus = (credential) => {
    if (!credential.expires_at) {
      return { status: "unknown", color: "warning", label: "Unknown" };
    }

    const expiresAt = new Date(credential.expires_at);
    const now = new Date();
    const timeDiff = expiresAt.getTime() - now.getTime();
    const daysDiff = Math.ceil(timeDiff / (1000 * 3600 * 24));

    if (timeDiff <= 0) {
      return { status: "expired", color: "error", label: "Expired" };
    } else if (daysDiff <= 7) {
      return {
        status: "expiring",
        color: "warning",
        label: `Expires in ${daysDiff} day(s)`,
      };
    } else {
      return {
        status: "valid",
        color: "success",
        label: `Valid (${daysDiff} days left)`,
      };
    }
  };

  return (
    <Grid container spacing={3}>
      <Grid item xs={12}>
        <Card
          sx={{
            border: "3px solid",
            borderColor: "divider",
            borderRadius: 2,
          }}
        >
          <CardContent sx={{ p: 4 }}>
            <Box
              display="flex"
              justifyContent="space-between"
              alignItems="center"
              mb={3}
            >
              <Typography variant="h6" sx={{ mr: 2 }}>
                Google API Credentials
              </Typography>
              <Box sx={{ display: "flex", gap: 1 }}>
                <Button
                  variant="outlined"
                  startIcon={<RefreshIcon />}
                  onClick={loadGoogleCredentials}
                  disabled={loading}
                >
                  Refresh
                </Button>
                <Button
                  variant="contained"
                  startIcon={<AddIcon />}
                  onClick={() => setCredentialDialog(true)}
                >
                  Add Credentials
                </Button>
              </Box>
            </Box>

            <Alert severity="info" sx={{ mb: 3 }}>
              <Typography variant="body2">
                <strong>How to Get Your Google Credentials:</strong>
              </Typography>
              <ol style={{ margin: 0, paddingLeft: "20px" }}>
                <li>Go to the Google Cloud Console</li>
                <li>Create a new project or select an existing one</li>
                <li>
                  Enable the APIs you want to use (Gmail, Drive, Sheets, etc.)
                </li>
                <li>Create credentials (OAuth 2.0 Client IDs)</li>
                <li>Download the credentials JSON file</li>
                <li>Upload the file here and complete the OAuth flow</li>
              </ol>
              <Typography variant="body2" mt={1}>
                For more information, visit{" "}
                <a
                  href="https://developers.google.com/workspace/guides/create-credentials"
                  target="_blank"
                  rel="noopener noreferrer"
                  style={{
                    color: "#1976d2",
                    textDecoration: "underline",
                    fontWeight: "bold",
                  }}
                >
                  Google Workspace API Documentation
                </a>
              </Typography>
            </Alert>

            <List
              sx={{
                width: "100%",
                border: "2px solid",
                borderColor: "divider",
                borderRadius: 1,
                bgcolor: "background.paper",
              }}
            >
              {loading && googleCredentials.length === 0 ? (
                <ListItem>
                  <Box
                    sx={{
                      display: "flex",
                      justifyContent: "center",
                      width: "100%",
                      py: 2,
                    }}
                  >
                    <CircularProgress />
                  </Box>
                </ListItem>
              ) : (
                <>
                  {googleCredentials.map((credential) => (
                    <ListItem
                      key={credential.id}
                      divider
                      sx={{
                        px: 4,
                        py: 3,
                        borderBottom: "3px solid",
                        borderColor: "divider",

                        borderLeft: credential.active_omni
                          ? "6px solid"
                          : "none",
                        borderLeftColor: credential.active_omni
                          ? "primary.main"
                          : "transparent",
                        "&:last-child": {
                          borderBottom: "none",
                        },
                      }}
                    >
                      <Grid container alignItems="center" spacing={2}>
                        <Grid item xs={8}>
                          <Typography variant="h6">
                            {credential.authenticated_email ||
                              credential.client_id ||
                              "Google Credential"}
                          </Typography>
                          <Typography
                            variant="body2"
                            sx={{
                              wordBreak: "break-all",
                              pr: 2,
                            }}
                          >
                            Client ID: {credential.client_id}
                          </Typography>
                          {credential.authenticated_email && (
                            <Typography
                              variant="body2"
                              sx={{
                                color: "primary.main",
                                pr: 2,
                              }}
                            >
                              Email: {credential.authenticated_email}
                            </Typography>
                          )}
                          <Box sx={{ mt: 1 }}>
                            <Typography
                              variant="caption"
                              color="text.secondary"
                            >
                              Scopes:{" "}
                              {Array.isArray(credential.scopes)
                                ? credential.scopes.join(", ")
                                : credential.scopes}
                            </Typography>
                          </Box>
                          <Box
                            sx={{
                              mt: 0.5,
                              display: "flex",
                              gap: 1,
                              alignItems: "center",
                              flexWrap: "wrap",
                            }}
                          >
                            <Chip
                              label={credential.status || "Unknown"}
                              size="small"
                              color={
                                credential.status === "authorized"
                                  ? "success"
                                  : "error"
                              }
                            />
                            {credential.expires_at && (
                              <Chip
                                label={getTokenStatus(credential).label}
                                size="small"
                                color={getTokenStatus(credential).color}
                                variant="outlined"
                              />
                            )}
                            <Typography
                              variant="caption"
                              color="text.secondary"
                            >
                              Created:{" "}
                              {credential.created_at
                                ? new Date(
                                    credential.created_at,
                                  ).toLocaleDateString()
                                : "Unknown"}
                            </Typography>
                          </Box>
                          {credential.expires_at && (
                            <Box sx={{ mt: 0.5 }}>
                              <Typography
                                variant="caption"
                                color="text.secondary"
                              >
                                Token expires:{" "}
                                {new Date(
                                  credential.expires_at,
                                ).toLocaleString()}
                              </Typography>
                            </Box>
                          )}
                        </Grid>
                        <Grid
                          item
                          xs={4}
                          sx={{
                            display: "flex",
                            justifyContent: "flex-end",
                            alignItems: "center",
                            gap: 0.5,
                          }}
                        >
                          {getTokenStatus(credential).status === "expired" && (
                            <IconButton
                              onClick={() =>
                                reauthorizeCredential(credential.id)
                              }
                              disabled={loading}
                              color="warning"
                              title="Reauthorize Expired Credential"
                            >
                              {loading ? (
                                <CircularProgress size={20} />
                              ) : (
                                <RefreshIcon />
                              )}
                            </IconButton>
                          )}
                          <FormControlLabel
                            control={
                              <Switch
                                checked={credential.active_omni || false}
                                onChange={() =>
                                  toggleActiveCredential(credential.id)
                                }
                                color="primary"
                                disabled={loading}
                              />
                            }
                            label={
                              credential.active_omni ? "Active" : "Inactive"
                            }
                            labelPlacement="top"
                            sx={{
                              mx: 1,
                              "& .MuiFormControlLabel-label": {
                                fontSize: "0.75rem",
                                color: "text.secondary",
                              },
                            }}
                          />
                          <IconButton
                            color="info"
                            onClick={() => handleReauthorize(credential.id)}
                            title="Reauthorize Credential"
                            disabled={loading}
                          >
                            <SecurityIcon />
                          </IconButton>
                          <IconButton
                            color="error"
                            onClick={() => deleteCredential(credential.id)}
                            title="Delete Credential"
                            disabled={loading}
                          >
                            <DeleteIcon />
                          </IconButton>
                        </Grid>
                      </Grid>
                    </ListItem>
                  ))}
                  {googleCredentials.length === 0 && !loading && (
                    <ListItem>
                      <ListItemText
                        primary="No credentials configured"
                        secondary="Click 'Add Credentials' to get started"
                        sx={{ textAlign: "center" }}
                      />
                    </ListItem>
                  )}
                </>
              )}
            </List>
          </CardContent>
        </Card>
      </Grid>
      {/* Auth URL Dialog */}
      <Dialog
        open={authUrlDialog}
        onClose={handleAuthDialogClose}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle
          sx={{
            display: "flex",
            alignItems: "center",
            gap: 1,
            bgcolor: "success.light",
            color: "white",
          }}
        >
          <CheckCircleIcon />
          Authentication Required
        </DialogTitle>
        <DialogContent sx={{ pt: 3 }}>
          <Alert severity="info" sx={{ mb: 3 }}>
            <Typography variant="body1" gutterBottom>
              <strong>Next Step:</strong> You need to authenticate with Google
              to complete the setup.
            </Typography>
          </Alert>

          <Typography variant="body1" gutterBottom>
            Click the button below to authenticate your Google credentials:
          </Typography>

          <Box
            sx={{
              display: "flex",
              justifyContent: "center",
              my: 3,
            }}
          >
            <Button
              variant="contained"
              size="large"
              color="primary"
              startIcon={<SecurityIcon />}
              onClick={() => window.open(authUrl, "_blank")}
              sx={{
                py: 1.5,
                px: 4,
                fontSize: "1.1rem",
                fontWeight: "bold",
                borderRadius: 2,
                textTransform: "none",
              }}
            >
              Authenticate Google Credentials
            </Button>
          </Box>

          <Alert severity="info" sx={{ mt: 2 }}>
            <Typography variant="body2">
              <strong>What happens next:</strong>
            </Typography>
            <ul style={{ margin: "8px 0", paddingLeft: "20px" }}>
              <li>A new tab will open with Google's authentication page</li>
              <li>Sign in with your Google account</li>
              <li>Grant the requested permissions</li>
              <li>You'll be redirected back automatically</li>
              <li>Close this dialog once authentication is complete</li>
            </ul>
          </Alert>

          <Typography
            variant="caption"
            color="text.secondary"
            sx={{ mt: 2, display: "block" }}
          >
            <strong>Auth URL:</strong> {authUrl}
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleAuthDialogClose} variant="outlined">
            Close
          </Button>
          <Button
            onClick={() => window.open(authUrl, "_blank")}
            variant="contained"
            startIcon={<SecurityIcon />}
          >
            Authenticate Now
          </Button>
        </DialogActions>
      </Dialog>

      {/* Google Credential Dialog */}
      <Dialog
        open={credentialDialog}
        onClose={() => {
          setCredentialDialog(false);
          setEditingCredential(null);
        }}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle>Add Google API Credentials</DialogTitle>
        <DialogContent>
          <Grid container spacing={2} sx={{ mt: 1 }}>
            <Grid item xs={12}>
              <Typography variant="h6" gutterBottom>
                API Scopes Configuration
              </Typography>
              <Typography variant="body2" color="text.secondary" paragraph>
                Select the Google API scopes needed for your use case. Scopes
                marked as "Restricted" require additional verification from
                Google.
              </Typography>

              {/* Group scopes by usage type */}
              {["Non-sensitive", "Sensitive", "Restricted", "Sheets"].map(
                (usageType) => {
                  const scopesForType = availableScopes.filter(
                    (scope) => scope.usage === usageType,
                  );
                  if (scopesForType.length === 0) return null;

                  return (
                    <Paper
                      key={usageType}
                      sx={{ p: 2, mb: 2, backgroundColor: "grey.50" }}
                    >
                      <Typography
                        variant="subtitle1"
                        fontWeight="bold"
                        gutterBottom
                      >
                        {usageType} Scopes
                      </Typography>
                      {scopesForType.map((scope) => (
                        <FormControlLabel
                          key={scope.value}
                          control={
                            <Switch
                              checked={credentialForm.scopes.includes(
                                scope.value,
                              )}
                              onChange={(e) => {
                                const isChecked = e.target.checked;
                                setCredentialForm((prev) => ({
                                  ...prev,
                                  scopes: isChecked
                                    ? [...prev.scopes, scope.value]
                                    : prev.scopes.filter(
                                        (s) => s !== scope.value,
                                      ),
                                }));
                              }}
                              color="primary"
                            />
                          }
                          label={
                            <Box>
                              <Typography variant="body2" fontWeight="medium">
                                {scope.label}
                              </Typography>
                              <Typography
                                variant="caption"
                                color="text.secondary"
                              >
                                {scope.description}
                              </Typography>
                            </Box>
                          }
                          sx={{
                            display: "flex",
                            alignItems: "flex-start",
                            width: "100%",
                            mb: 1,
                            "& .MuiFormControlLabel-label": {
                              ml: 1,
                            },
                          }}
                        />
                      ))}
                    </Paper>
                  );
                },
              )}
            </Grid>

            <Grid item xs={12}>
              <Box
                sx={{
                  border: "2px dashed",
                  borderColor: "divider",
                  borderRadius: 1,
                  p: 3,
                  textAlign: "center",
                  bgcolor: "background.paper",
                }}
              >
                <input
                  type="file"
                  accept=".json"
                  style={{ display: "none" }}
                  id="credentials-file"
                  onChange={handleFileUpload}
                />
                <label htmlFor="credentials-file">
                  <Box sx={{ cursor: "pointer" }}>
                    <UploadIcon
                      sx={{ fontSize: 48, color: "text.secondary", mb: 1 }}
                    />
                    <Typography variant="h6" color="text.secondary">
                      Upload Credentials JSON File
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      {uploadedFile
                        ? uploadedFile.name
                        : "Click to select your Google Cloud Console credentials file"}
                    </Typography>
                  </Box>
                </label>
              </Box>
            </Grid>

            {uploadedFile && (
              <Grid item xs={12}>
                <Alert severity="success">
                  <Typography variant="body2">
                    File uploaded: {uploadedFile.name}
                  </Typography>
                </Alert>
              </Grid>
            )}
          </Grid>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setCredentialDialog(false)}>Cancel</Button>
          <Button
            onClick={handleCredentialSubmit}
            variant="contained"
            disabled={loading || !uploadedFile}
          >
            {loading ? <CircularProgress size={20} /> : "Start OAuth Flow"}
          </Button>
        </DialogActions>
      </Dialog>
    </Grid>
  );
};

export default GoogleCredentials;
